import React, {useEffect, useRef, useState} from 'react';
import {Card, CardContent, Button, Typography, Box, CircularProgress, LinearProgress, Tooltip, Dialog, DialogContent} from '@mui/material';
import { Stars } from '@material-ui/icons';
import Icon1 from '../../../assets/images/DQProImages/IconImg1.png';
import Icon2 from '../../../assets/images/DQProImages/IconImg2.png';
import Icon4 from '../../../assets/images/DQProImages/IconImg4.png';
import useStyles from '../../../assets/styles/ProgramCreatorStyles';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {
    buildLearningProgramService, generateLearningProgramContentService,
    getInsiderProgramService,
    getLearningProgramPreviewStatusService, getLearningProgramStatusService,
    getLearningProgramPlansService,
    publishLearningProgramService,
    linkLearningProgramService,
} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { updateProgramRequestStatusService } from "../../../services/client.service";
import {ENABLE_EARLY_PREVIEW, DQ_PRO_PLAN_KEY, REQUEST_TYPE, TOAST_TYPES, LEARNING_PROGRAM_STATUS, WORKSPACE_REQUEST_STATUS, USER_ACCESS_LEVEL} from "../../../utils/constants";
import { isClientWorkspace } from "../../../utils/capabilities";
import {toast, toastWarning} from "../../../utils/utils";
import {useAuth} from "../../../context/auth";
import ProgramPublishBadge from "../components/ProgramPublishBadge.comp";
import PublishProgramDialogs from "../components/PublishProgramDialogs.comp";

const PUBLISH_TOOLTIP = 'Publish this learning program to make it accessible to assigned schools. Once published, the program can no longer be edited.';

const OUTLINE_STEPS = [
    "Generating program modules and descriptions.",
    "Generating program units and descriptions.",
    "Generating program sessions and descriptions.",
    "Generating program session learning objectives.",
    "Applying learner context.",
    "Organizing program outline.",
    "Mapping with DQ Codes.",
    "Identifying related frameworks."
];

const CONTENT_GENERATION_STEPS = [
    "Generating learning content",      // 0%
    "Generating images",                // 20%
    "Generating activities",            // 40%
    "Generating videos",                // 60%
    "Organizing program sessions",      // 80%
    "Complete"                          // 100%
];

const STATUS_TO_STEP_INDEX = {
    1: 1, // steps 0-1 → "modules" + "units"
    2: 3, // steps 2-3 → "sessions" + "learning objectives"
    3: 4, // step 4 → "applying learner context"
    4: 5, // step 5 → "organizing program outline"
    5: 6, // step 6 → "mapping with DQ Codes"
    6: 6, // step 6 → still mapping with DQ Codes
    7: 7, // step 7 → "identifying related frameworks" (final step, completion)
};

function deriveStepIndexFromStatus(progressStatus) {
    if (!progressStatus || progressStatus < 1) return 0;
    return STATUS_TO_STEP_INDEX[Math.min(progressStatus, 7)] ?? 0;
}

function derivePercentFromStatus(isReady, progressStatus) {
    if (isReady || progressStatus === 7) return 100;
    // Spread 1..6 across 95% (reserve the last 5% for the final "ready" flip)
    if (!progressStatus) return 0; // not started yet
    const clamped = Math.max(1, Math.min(progressStatus, 6));
    return Math.round((clamped / 6) * 95);
}

/**
 * Calculate content generation progress based on session completion status
 * @param {Object} contentStatus - Object with session keys and their status/ProgressStatus
 * @returns {Object} - { completedCount, totalCount, progressPercent, stepIndex, currentSession, sessionProgress }
 */
function calculateContentProgress(contentStatus) {
    if (!contentStatus || typeof contentStatus !== 'object') {
        return {
            completedCount: 0,
            totalCount: 0,
            progressPercent: 0,
            stepIndex: 0,
            currentSession: 0,
            sessionProgress: 0
        };
    }

    const sessions = Object.keys(contentStatus);
    const totalCount = sessions.length;

    // Handle both old format (string) and new format (object with status and ProgressStatus)
    let completedCount = 0;
    let currentSessionIndex = 0;
    let currentSessionProgress = 0;

    sessions.forEach((key, index) => {
        const sessionData = contentStatus[key];

        // Check if new format (object) or old format (string)
        const status = typeof sessionData === 'object' ? sessionData.status : sessionData;
        const progressStatus = typeof sessionData === 'object' ? sessionData.ProgressStatus : null;

        if (status === 'done' || progressStatus === 7) {
            completedCount++;
        } else if (status === 'in_progress' && progressStatus > 0) {
            // This is the current session being worked on. progressStatus > 0
            // (not just truthy) — a ProgressStatus of -1 means the session
            // failed, and letting it through here produced a -14% progress bar.
            currentSessionIndex = index + 1;
            // ProgressStatus is 1-7, convert to percentage (inverse scale)
            // 1 = just started (~14%), 7 = completed (100%)
            currentSessionProgress = Math.round((progressStatus / 7) * 100);
        }
    });

    if (totalCount === 0) {
        return {
            completedCount: 0,
            totalCount: 0,
            progressPercent: 0,
            stepIndex: 0,
            currentSession: 0,
            sessionProgress: 0
        };
    }

    // Calculate overall progress including partial progress of current session
    const overallProgress = completedCount + (currentSessionProgress / 100);
    const progressPercent = Math.round((overallProgress / totalCount) * 100);

    // Map progress to steps: 0-20%, 21-40%, 41-60%, 61-80%, 81-99%, 100%
    let stepIndex;
    if (progressPercent === 100) {
        stepIndex = 5; // Complete
    } else if (progressPercent >= 80) {
        stepIndex = 4; // Organizing program sessions
    } else if (progressPercent >= 60) {
        stepIndex = 3; // Generating videos
    } else if (progressPercent >= 40) {
        stepIndex = 2; // Generating activities
    } else if (progressPercent >= 20) {
        stepIndex = 1; // Generating images
    } else {
        stepIndex = 0; // Generating learning content
    }

    return {
        completedCount,
        totalCount,
        progressPercent,
        stepIndex,
        currentSession: currentSessionIndex,
        sessionProgress: currentSessionProgress
    };
}

/**
 * Detect sessions that are stuck in "in_progress" with null ProgressStatus
 * while at least one session is already "done" — indicates a free-plan build
 * that was only partially run (session 1 only) and now needs a retry after upgrade.
 */
function hasStuckSessions(contentStatusData) {
    if (!contentStatusData || typeof contentStatusData !== 'object') return false;
    const sessions = Object.values(contentStatusData);
    const hasDone = sessions.some(s => {
        const st = typeof s === 'object' ? s.status : s;
        return st === 'done';
    });
    const hasStuck = sessions.some(s => {
        if (typeof s !== 'object') return false;
        return s.status === 'in_progress' && (s.ProgressStatus === null || s.ProgressStatus === undefined);
    });
    return hasDone && hasStuck;
}

/**
 * A ProgressStatus of -1 on any session means the server gave up on it — same
 * signal the AI-builder's own polling stops on. Without this check
 * calculateContentProgress's math never surfaces a failure (it just silently
 * excludes the failed session from the percentage), so the dashboard would
 * poll forever showing partial progress instead of the real error state.
 */
export function hasFailedContentSession(contentStatusData) {
    if (!contentStatusData || typeof contentStatusData !== 'object') return false;
    return Object.values(contentStatusData).some(s =>
        typeof s === 'object' && s.ProgressStatus === -1
    );
}

const DQProProgDashboardPage = () => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const { code } = useParams();
    const { authUser } = useAuth();
    const isLevel22 = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    // True when navigated here from EditProgramInfoDialog after a retry-build.
    // Suppresses the "Plan Upgraded!" needsRebuild overlay so sessions going
    // in_progress during the rebuild aren't misidentified as stuck.
    const isRebuildFromEdit = location.state?.isRebuildFromEdit || false;
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [loading, setLoading] = useState(!location.state?.assessment);
    const [outlineStatus, setOutlineStatus] = useState({
        is_outline_ready: null,
        generation_initiated: null,
        failed: false,
        failure_reason: '',
        ml_server_resp: null
    });
    const [previewEnabled, setPreviewEnabled] = useState(false);
    const [dqcoList, setDqcoList] = useState(location.state?.dqcoList || []);
    const [contentStatus, setContentStatus] = useState({
        ml_server_resp: null
    });
    // Set when a content session's ProgressStatus reads -1 (server gave up on
    // that session) — checked ahead of ml_server_resp in hasContentError()
    // since that field alone never carried this failure.
    const [contentFailed, setContentFailed] = useState(false);

    // local UI progress for outline generation
    const [outlineStepIndex, setOutlineStepIndex] = useState(0);
    const [outlineProgress, setOutlineProgress] = useState(0);

    // Content generation progress tracking
    const [contentProgress, setContentProgress] = useState({
        completedCount: 0,
        totalCount: 0,
        progressPercent: 0,
        stepIndex: 0,
        currentSession: 0,
        sessionProgress: 0
    });

    // Whether the program needs a rebuild request to process remaining sessions
    // (happens when user upgrades from default plan and existing sessions are stuck)
    const [needsRebuild, setNeedsRebuild] = useState(false);

    // Publish state — LEARNING_PROGRAM_STATUS.DRAFT or PUBLISHED, possibly other
    // in-between values in the future. Defaults to the program's own field if already
    // loaded (e.g. navigated here with state), refreshed below.
    const [programStatus, setProgramStatus] = useState(location.state?.assessment?.status ?? LEARNING_PROGRAM_STATUS.DRAFT);
    const [publishing, setPublishing] = useState(false);
    // null = both dialogs closed; 'confirm' = "are you sure?" step; 'success' = post-publish step.
    const [publishStage, setPublishStage] = useState(null);
    // School whose learning-program request THIS publish fulfilled — switches the
    // publish-success dialog to the "linked to (school)" copy (slide 155).
    const [deliveredSchoolName, setDeliveredSchoolName] = useState('');
    // Org of the school this publish fulfilled a request for. Lets us link the
    // program straight to them instead of sending the owner to the client list
    // to hunt for the school (bug report, slide 40).
    const [deliveredOrgCode, setDeliveredOrgCode] = useState('');
    // Confirm WHICH school before linking, so the owner can see they are linking
    // to the right one (Lydia, slide 40 thread).
    const [linkConfirmOpen, setLinkConfirmOpen] = useState(false);
    const [linkingToSchool, setLinkingToSchool] = useState(false);

    // Current plan key — used to decide whether to apply free-plan early-preview logic.
    // A ref is kept in sync so async polling loops always read the latest value.
    const planKeyRef = useRef(null);
    useEffect(() => {
        getLearningProgramPlansService()
            .then(res => {
                if (res?.data?.current_plan?.key) {
                    planKeyRef.current = res.data.current_plan.key;
                }
            })
            .catch(() => {});
    }, []);


    // Keep programStatus in sync whenever programData is (re)loaded.
    useEffect(() => {
        if (programData && programData.status !== undefined && programData.status !== null) {
            setProgramStatus(Number(programData.status));
        }
    }, [programData]);

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, code]);

    useEffect(() => {
        const fetchAllStatuses = async () => {
            setLoading(true);
            setPreviewEnabled(false);
            try {
                // Fetch plan and outline status in parallel for reliability
                const [planRes, outlineRes] = await Promise.all([
                    getLearningProgramPlansService().catch(() => null),
                    getLearningProgramPreviewStatusService(code)
                ]);

                // Persist plan key for polling loops
                const planKey = planRes?.data?.current_plan?.key || null;
                if (planKey) planKeyRef.current = planKey;

                const isOutlineReady = outlineRes?.data?.is_outline_ready;
                const generationInitiated = outlineRes?.data?.generation_initiated;
                const progressStatus = outlineRes?.data?.progress_status; // 1..7
                const failureReason = outlineRes?.data?.error || '';
                const failed = progressStatus === -1;
                const mlServerResp = outlineRes?.data?.ml_server_resp;

                setOutlineStatus({
                    is_outline_ready: isOutlineReady,
                    generation_initiated: generationInitiated,
                    failed,
                    failure_reason: failureReason,
                    ml_server_resp: mlServerResp
                });

                // Drive UI from API
                if (failed) {
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                } else {
                    setOutlineStepIndex(isOutlineReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus));
                    setOutlineProgress(derivePercentFromStatus(isOutlineReady, progressStatus));
                }

                // Content generation status
                if (generationInitiated) {
                    const contentRes = await getLearningProgramStatusService(code);
                    const contentStatusData = contentRes?.data?.contentStatus || {};
                    const contentMlServerResp = contentRes?.data?.ml_server_resp;

                    setContentStatus({
                        ml_server_resp: contentMlServerResp
                    });

                    const progress = calculateContentProgress(contentStatusData);
                    setContentProgress(progress);

                    // Failure: mark failed, don't enable preview or start polling.
                    if (hasFailedContentSession(contentStatusData)) {
                        setContentFailed(true);
                        return;
                    }

                    // Detect stuck sessions after plan upgrade (non-default plan + stuck sessions).
                    // Skip when we arrived here from an edit-rebuild — sessions are expectedly in_progress.
                    const isFreePlan = !planKey || planKey === DQ_PRO_PLAN_KEY.DEFAULT;
                    if (!isFreePlan && !isRebuildFromEdit && hasStuckSessions(contentStatusData)) {
                        setNeedsRebuild(true);
                        return; // Don't enable preview — user must trigger rebuild
                    }

                    // Enable preview based on plan, feature flag, or full completion.
                    // Free/default plan: server only builds session 1 — treat as done when
                    // at least 1 session is complete regardless of NumSessions selected.
                    if (isFreePlan && progress.completedCount >= 1) {
                        setPreviewEnabled(true);
                    } else if (ENABLE_EARLY_PREVIEW) {
                        const m1u1s1Data = contentStatusData['M1_U1_S1'];
                        const isM1U1S1Done = typeof m1u1s1Data === 'object'
                            ? m1u1s1Data.status === 'done'
                            : m1u1s1Data === 'done';
                        const hasMinimumProgress = progress.progressPercent >= 50;
                        setPreviewEnabled(isM1U1S1Done && hasMinimumProgress);
                    } else {
                        setPreviewEnabled(progress.progressPercent === 100);
                    }
                }
            } catch (e) {
                // Handle error
            } finally {
                setLoading(false);
            }
        };

        if (code) fetchAllStatuses();
    }, [code]);

    // Polling effect
    useEffect(() => {
        let intervalId;
        if (code && !outlineStatus.failed && (outlineStatus.is_outline_ready !== true || outlineStatus.generation_initiated !== true)) {
            intervalId = setInterval(async () => {
                const response = await getLearningProgramPreviewStatusService(code);
                const isReady = response?.data?.is_outline_ready;
                const initiated = response?.data?.generation_initiated;
                const progressStatus = response?.data?.progress_status; // 1..7
                const failureReason = response?.data?.error || '';
                const mlServerResp = response?.data?.ml_server_resp;

                // Failure: mark failed and stop polling
                if (progressStatus === -1) {
                    setOutlineStatus({
                        is_outline_ready: isReady,
                        generation_initiated: initiated,
                        failed: true,
                        failure_reason: failureReason,
                        ml_server_resp: mlServerResp
                    });
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                    clearInterval(intervalId);
                    return;
                }

                setOutlineStatus({
                    is_outline_ready: isReady,
                    generation_initiated: initiated,
                    failed: false,
                    failure_reason: '',
                    ml_server_resp: mlServerResp
                });

                setOutlineStepIndex(
                    isReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus)
                        );
                setOutlineProgress(
                    derivePercentFromStatus(isReady, progressStatus)
                    );
                if (response?.data?.is_outline_ready && response?.data?.generation_initiated) {
                    clearInterval(intervalId);
                }
            }, 5000);
        }
        return () => clearInterval(intervalId);
    }, [code, outlineStatus.is_outline_ready, outlineStatus.generation_initiated, outlineStatus.failed]);

    // Polling effect for content generation
    useEffect(() => {
        let intervalId;

        if (
            outlineStatus.generation_initiated &&
            !outlineStatus.failed &&
            !contentFailed &&
            !previewEnabled && // Only poll if not yet enabled
            !needsRebuild      // Stop polling when rebuild is needed
        ) {
            intervalId = setInterval(async () => {
                try {
                    const contentRes = await getLearningProgramStatusService(code);
                    const contentStatusData = contentRes?.data?.contentStatus || {};
                    const contentMlServerResp = contentRes?.data?.ml_server_resp;

                    setContentStatus({
                        ml_server_resp: contentMlServerResp
                    });

                    const progress = calculateContentProgress(contentStatusData);
                    setContentProgress(progress);

                    // Failure: mark failed and stop polling.
                    if (hasFailedContentSession(contentStatusData)) {
                        setContentFailed(true);
                        clearInterval(intervalId);
                        return;
                    }

                    // Detect stuck sessions after plan upgrade.
                    // Skip when we arrived here from an edit-rebuild — sessions are expectedly in_progress.
                    const isFreePlan = !planKeyRef.current || planKeyRef.current === DQ_PRO_PLAN_KEY.DEFAULT;
                    if (!isFreePlan && !isRebuildFromEdit && hasStuckSessions(contentStatusData)) {
                        setNeedsRebuild(true);
                        clearInterval(intervalId);
                        return;
                    }

                    // Enable preview based on plan, feature flag, or full completion.
                    // Free/default plan: server only builds session 1 — treat as done when
                    // at least 1 session is complete regardless of NumSessions selected.
                    if (isFreePlan && progress.completedCount >= 1) {
                        setPreviewEnabled(true);
                        clearInterval(intervalId);
                    } else if (ENABLE_EARLY_PREVIEW) {
                        const m1u1s1Data = contentStatusData['M1_U1_S1'];
                        const isM1U1S1Done = typeof m1u1s1Data === 'object'
                            ? m1u1s1Data.status === 'done'
                            : m1u1s1Data === 'done';
                        const hasMinimumProgress = progress.progressPercent >= 50;
                        if (isM1U1S1Done && hasMinimumProgress) {
                            setPreviewEnabled(true);
                            clearInterval(intervalId);
                        }
                    } else {
                        if (progress.progressPercent === 100) {
                            setPreviewEnabled(true);
                            clearInterval(intervalId);
                        }
                    }
                } catch (e) {
                    console.error('Error polling content status:', e);
                }
            }, 10000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [code, outlineStatus.generation_initiated, previewEnabled, outlineStatus.failed, contentFailed, needsRebuild]);

    // Check if outline has error based on ml_server_resp
    const hasOutlineError = () => {
        if (outlineStatus.ml_server_resp) {
            const mlResp = outlineStatus.ml_server_resp;
            return mlResp.error === true || (mlResp.http_code && mlResp.http_code !== 200) || (mlResp.status_code && mlResp.status_code !== 200);
        }
        return false;
    };

    // Check if content generation has error based on ml_server_resp
    const hasContentError = () => {
        if (contentFailed) return true;
        if (contentStatus.ml_server_resp) {
            const mlResp = contentStatus.ml_server_resp;
            return mlResp.error === true || (mlResp.http_code && mlResp.http_code !== 200) || (mlResp.status_code && mlResp.status_code !== 200);
        }
        return false;
    };

    // Handler to rebuild the outline if not generated yet
    const handleReBuildOutline = async () => {
        const request_type = REQUEST_TYPE.RETRY;
        setLoading(true);
        try {
            const response = await buildLearningProgramService(
                code,
                programData.full_name,
                programData.target_audience,
                programData.dq_codes,
                programData.time_to_complete_minutes,
                programData.image_url,
                programData.theme_color,
                programData.badge_background,
                programData.primary_color,
                programData.description,
                programData.more_info,
                programData.translations,
                programData.program_details,
                request_type,
            );

            // Reset outline state to restart polling
            setOutlineStatus({
                is_outline_ready: false,
                generation_initiated: false,
                failed: false,
                failure_reason: '',
                ml_server_resp: null
            });
            setOutlineStepIndex(0);
            setOutlineProgress(0);
        } catch (error) {
            console.error('Error building outline:', error);
            toastWarning('Failed to build program outline');
        } finally {
            setLoading(false);
        }
    };

    // Handler to regenerate program content if outline is ready but content not generated
    const handleReGenerateContent = async () => {
        const request_type = REQUEST_TYPE.RETRY;
        setLoading(true);
        try {
            const response = await generateLearningProgramContentService(code, request_type);

            // Reset content generation state to restart polling
            setPreviewEnabled(false);
            setContentFailed(false);
            setContentProgress({
                completedCount: 0,
                totalCount: 0,
                progressPercent: 0,
                stepIndex: 0,
                currentSession: 0,
                sessionProgress: 0
            });
            setContentStatus({
                ml_server_resp: null
            });
        } catch (error) {
            console.error('Error generating program content:', error);
            toastWarning('Failed to generate program content');
        } finally {
            setLoading(false);
        }
    };

    // Handler to rebuild remaining sessions after a plan upgrade.
    // Called when the user explicitly clicks "Resume Building" on the dashboard.
    const handleRebuildRemaining = async () => {
        setLoading(true);
        try {
            await generateLearningProgramContentService(code, 'retry');
            // Clear the rebuild flag so polling resumes
            setNeedsRebuild(false);
            setPreviewEnabled(false);
            setContentProgress({
                completedCount: 0,
                totalCount: 0,
                progressPercent: 0,
                stepIndex: 0,
                currentSession: 0,
                sessionProgress: 0
            });
            setContentStatus({ ml_server_resp: null });
        } catch (error) {
            console.error('Error rebuilding remaining sessions:', error);
            toastWarning('Failed to rebuild remaining sessions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Determine button labels and actions based on current state
    const getOutlineButtonConfig = () => {
        if (hasOutlineError() || outlineStatus.failed) {
            return { label: 'Retry', action: handleReBuildOutline };
        }
        // Published programs are one-way — editing is disabled, so drop "Edit" from the label.
        // Same for a school / network viewing its partner's program: view only.
        if (programStatus === LEARNING_PROGRAM_STATUS.PUBLISHED || isPartnerProgramForClient) {
            return { label: 'View', url: `/dq_pro/products/program/view/${code}` };
        }
        return { label: 'View/Edit', url: `/dq_pro/products/program/view/${code}` };
    };

    const getContentButtonConfig = () => {
        if (needsRebuild) {
            return { label: 'Resume Building', action: handleRebuildRemaining };
        }
        if (hasContentError()) {
            return { label: 'Retry', action: handleReGenerateContent };
        }
        return { label: 'Preview', url: `/dq_pro/program/creation/preview` };
    };

    // Publish is one-way (no unpublish) and requires content generation to be
    // fully complete — mirrors the Resources card's own readiness gate.
    const isProgramPublished = programStatus === LEARNING_PROGRAM_STATUS.PUBLISHED;
    /*
     * A school or network viewing a program its PARTNER built (SW-46 / SNW-64):
     * "Schools cannot edit the program, they can only view a preview" — they
     * ask their partner for changes; only the partner publishes. Level 22 alone missed a
     * network (a client at 21). A P1B school's own program is not affected.
     */
    const isPartnerProgramForClient = isClientWorkspace(authUser)
        && !!programData?.created_by_user_id
        && String(programData.created_by_user_id) !== String(authUser?._id);
    const hidePublish = isLevel22 || isPartnerProgramForClient;
    const canPublish = !hidePublish && !isProgramPublished && !needsRebuild && !hasContentError()
        && previewEnabled && outlineStatus.is_outline_ready === true;

    const openPublishConfirm = () => {
        if (!code || publishing || isProgramPublished || !canPublish) return;
        setPublishStage('confirm');
    };

    const handlePublish = async () => {
        if (!code || publishing || isProgramPublished) return;
        setPublishing(true);
        try {
            const response = await publishLearningProgramService(code);
            if (response?.data) {
                // A successful call to this one-way publish endpoint always means the
                // program is now published — see DQProProgCreationPreviewV2.page.jsx for
                // why we don't trust response.data.status here.
                setProgramStatus(LEARNING_PROGRAM_STATUS.PUBLISHED);
                setProgramData((prev) => (prev ? { ...prev, status: LEARNING_PROGRAM_STATUS.PUBLISHED } : prev));
                setPublishStage('success');
                toast(TOAST_TYPES.SUCCESS, 'Published', response.data.already_published
                    ? 'This program is already published.'
                    : 'Learning program published successfully.');

                // If this program was built for a School's learning-program request,
                // mark that request DELIVERED (the API emails the school). The request
                // is resolved SERVER-SIDE from the program code, so this works in any
                // session; the browser stash is only used for the dialog's school
                // name. No-op for ordinary programs. Best effort — never disturbs
                // the publish result.
                updateProgramRequestStatusService({
                    program_code: code,
                    status: WORKSPACE_REQUEST_STATUS.BUILT,
                }).catch(() => {});
                try {
                    const rawStash = sessionStorage.getItem('dq_pending_program_request_id');
                    if (rawStash) {
                        let stash;
                        try { stash = JSON.parse(rawStash); } catch { stash = { id: rawStash }; }
                        if (stash?.id && (!stash.program_code || stash.program_code === code)) {
                            sessionStorage.removeItem('dq_pending_program_request_id');
                            if (stash.school_name) setDeliveredSchoolName(stash.school_name);
                            if (stash.org_code) setDeliveredOrgCode(stash.org_code);
                        }
                    }
                } catch { /* ignore storage errors */ }
            }
        } catch (err) {
            // invokeApi already surfaces the backend error message as a toast.
            setPublishStage(null);
        } finally {
            setPublishing(false);
        }
    };

    const handleGoToMaterials = () => setPublishStage(null);

    /**
     * When this publish fulfilled a school's request we know exactly which org to
     * link to, so confirm the school and link right here. An ordinary publish (no
     * requesting school) still falls back to the client list, unchanged.
     */
    const handleLinkToSchool = () => {
        setPublishStage(null);
        if (deliveredOrgCode && code) {
            setLinkConfirmOpen(true);
            return;
        }
        history.push('/dq_pro/clients/list');
    };

    /** Perform the link once the owner has confirmed the school. */
    const confirmLinkToSchool = async () => {
        if (!deliveredOrgCode || !code || linkingToSchool) return;
        setLinkingToSchool(true);
        try {
            const res = await linkLearningProgramService(deliveredOrgCode, [code]);
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Linked',
                    `${deliveredSchoolName || 'The school'} can now open this program from their workspace.`);
                setLinkConfirmOpen(false);
            }
        } catch {
            /* invokeApi surfaces the backend error (e.g. no license seats) */
        } finally {
            setLinkingToSchool(false);
        }
    };

    const cardsData = [
        {
            icon: Icon1,
            title: 'Program Outline',
            content: 'Overview of your curated program',
            buttons: [getOutlineButtonConfig()],
            height: '40px',
            width: '70px'
        },
        {
            icon: Icon2,
            title: 'Your Program',
            content: 'Preview your ready-to-go online program',
            buttons: [getContentButtonConfig()],
            height: '45px',
            width: '55px'
        },
        {
            icon: Icon4,
            title: 'Resources',
            content: 'Everything you need to run your program online, offline, or integrate with your system via API',
            buttons: [
                {
                    label: 'Access',
                    url: `/dq_pro/products/program/${code}/resources`
                }
            ],
            height: '45px',
            width: '52px'
        }
    ];

    const activeStep = 2;

    return (
        <div className={classes.container}>
            <Typography variant="h4" className={classes.title}>
                Here’s your program!
            </Typography>
            {isProgramPublished ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <ProgramPublishBadge status={programStatus} />
                </Box>
            ) : (
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                    background: 'var(--brand-surface-subtle, #FEF6E9)', border: '1px solid #FBE4B8',
                    borderRadius: '12px', px: 2.5, py: 1.75, mb: 3,
                    maxWidth: '560px', mx: 'auto',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ProgramPublishBadge status={programStatus} />
                        <Box>
                            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#052F44', lineHeight: 1.3 }}>
                                Not published yet
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: '#6A7282' }}>
                                {isPartnerProgramForClient
                                    ? 'Created by your DQ Partner, who will publish it. You can request changes from My Programs.'
                                    : 'Publish to make it accessible to assigned schools.'}
                            </Typography>
                        </Box>
                    </Box>
                    {!hidePublish && (
                        <Tooltip title={PUBLISH_TOOLTIP} arrow placement="top">
                            <span>
                                <Button
                                    onClick={openPublishConfirm}
                                    disabled={!canPublish || publishing}
                                    sx={{
                                        background: (!canPublish || publishing) ? '#BDBDBD' : '#FF6900',
                                        color: '#fff !important',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: '999px',
                                        px: 2.5,
                                        boxShadow: 'none',
                                        '&:hover': { background: (!canPublish || publishing) ? '#BDBDBD' : '#c9541e' },
                                        '&.Mui-disabled': { background: '#BDBDBD', color: '#fff !important' },
                                    }}
                                >
                                    {publishing ? 'Publishing...' : 'Publish'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}
            <PublishProgramDialogs
                stage={publishStage}
                publishing={publishing}
                onGoBack={() => setPublishStage(null)}
                onConfirmPublish={handlePublish}
                onGoToMaterials={handleGoToMaterials}
                onLinkToSchool={handleLinkToSchool}
                deliveredSchoolName={deliveredSchoolName}
            />

            {/* One step before the link (Lydia, slide 40 thread): name the school
                so the owner can see they are linking to the right one, then link
                from here — no detour through the client list. */}
            <Dialog
                open={linkConfirmOpen}
                onClose={() => !linkingToSchool && setLinkConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--brand-text, #052F44)', mb: 1.5 }}>
                        Program published
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--brand-text, #555)', mb: 3 }}>
                        Link it to <strong>{deliveredSchoolName || 'this school'}</strong> so they
                        can open it from their workspace.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setLinkConfirmOpen(false)}
                            disabled={linkingToSchool}
                            sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 700, px: 2, fontSize: 13, lineHeight: 1.2 }}
                        >
                            Later
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={confirmLinkToSchool}
                            disabled={linkingToSchool}
                            sx={{
                                background: '#FF6900', color: '#fff', borderRadius: '50px',
                                textTransform: 'none', fontWeight: 700, px: 2, fontSize: 13,
                                whiteSpace: 'nowrap', boxShadow: 'none',
                                '&:hover': { background: '#c9541e' },
                                '&.Mui-disabled': { background: '#FF6900', color: '#fff', opacity: 0.6 },
                            }}
                        >
                            {linkingToSchool ? 'Linking…' : 'Link Program to School'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Box sx={{flexDirection:{xs:'column', md:'row'}, padding: {xs:'0px',md:'0px',lg:'0px 40px',xl:'0px 110px'}, alignItems:{xs:'center', md:'stretch'}, }} style={{ display: 'flex', justifyContent: 'space-around', }}>
                {cardsData.map((step, index) => {
                    let showLoader = false;
                    let disabled = true;
                    let hasError = false;

                    if (index === 0) { // Program Outline
                        hasError = hasOutlineError() || outlineStatus.failed;
                        showLoader = !hasError && outlineStatus.is_outline_ready !== true;
                        // Enable button if there's an error (for regeneration) or if outline is ready
                        disabled = !hasError && outlineStatus.is_outline_ready !== true;
                    } else if (index === 1) { // Program Preview
                        hasError = hasContentError();
                        showLoader = !hasError && !needsRebuild && outlineStatus.generation_initiated && !previewEnabled;
                        // Enable button if error, needsRebuild, or preview is ready
                        disabled = !hasError && !needsRebuild && (!previewEnabled || outlineStatus.is_outline_ready !== true);
                    } else if (index === 2) { // Resources
                        // Resources card is enabled only when Program Preview (2nd card) is enabled
                        disabled = !previewEnabled || outlineStatus.is_outline_ready !== true;
                    }
                    return (
                    <Card
                        key={index}
                        className={`${classes.card}`}
                        style={{
                            pointerEvents: index <= activeStep ? 'auto' : 'none',
                            position: 'relative',
                                transition: 'opacity 0.3s ease, filter 0.3s ease'
                        }}
                    >
                            {(disabled || showLoader || hasError || (index === 1 && needsRebuild)) && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: (hasError || (index === 1 && needsRebuild)) ? 'rgba(255, 235, 238, 0.41)' : 'rgba(202,199,199,0.41)',
                                backdropFilter: 'blur(2px)',
                                zIndex: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 1,
                                filter: 'none'
                            }} />
                        )}

                        {/* Rebuild-needed overlay — shown when user upgraded but sessions are stuck */}
                        {index === 1 && needsRebuild && !hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 'calc(100% - 70px)',
                                    backgroundColor: 'rgba(250, 248, 244, 0.98)',
                                    zIndex: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 3,
                                    textAlign: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {/* Accent bar at top */}
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0,
                                    height: '4px',
                                    background: '#FF6900',
                                    borderRadius: '4px 4px 0 0',
                                }} />

                                {/* Icon */}
                                <Box sx={{
                                    width: 44, height: 44,
                                    borderRadius: '50%',
                                    background: 'rgba(255, 105, 0, 0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    mb: 1,
                                }}>
                                    <Stars style={{ fontSize: 32, color: '#FF6900' }} />
                                </Box>

                                <Typography variant="h6" sx={{
                                    fontWeight: 800, mb: 0.5, fontSize: '17px',
                                    color: '#FF6900',
                                }}>
                                    Plan Upgraded!
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#1a1a1a', mb: 1, fontWeight: 600, fontSize: '13px' }}>
                                    Your remaining sessions are ready to be built.
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', mb: 1, lineHeight: 1.5, fontSize: '12px' }}>
                                    Only the first session was generated on your previous plan.
                                    Click <strong style={{ color: '#FF6900' }}>Resume Building</strong> to generate the full program.
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#1a1a1a', mb: 1, fontWeight: 600, fontSize: '13px' }}>
                                    Please note: Resuming this process will use credits from your account.
                                </Typography>
                                <Box sx={{
                                    mt: 0.5, px: 2, py: 0.5,
                                    backgroundColor: 'rgba(255,105,0,0.08)',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255,105,0,0.2)',
                                }}>
                                    <Typography variant="caption" sx={{ color: '#FF6900', fontWeight: 600, fontSize: '11px' }}>
                                        {contentProgress.completedCount}/{contentProgress.totalCount} sessions completed
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Loader overlay */}
                        {showLoader && !hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: showLoader ? 'rgba(202,199,199,0.7)' : 'rgba(202,199,199,0.41)',
                                    backdropFilter: 'blur(2px)',
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 1,
                                    filter: 'none'
                                }}
                            >
                                    {showLoader && (
                                        <>
                                            <CircularProgress sx={{ color: '#FF6900' }}/>
                                            {index === 0 ? (
                                                <>
                                                    {/* Active step only */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            color: outlineStatus.is_outline_ready ? '#2e7d32' : '#FF6900',
                                                            fontWeight: 700,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {outlineStatus.is_outline_ready
                                                            ? 'Outline ready. Finalizing…'
                                                            : OUTLINE_STEPS[Math.min(outlineStepIndex, OUTLINE_STEPS.length - 1)]
                                                        }
                                                    </Typography>
                                                    <Box sx={{ width: '80%', mb: 1 }}>
                                                        <LinearProgress variant="determinate" value={outlineProgress} sx={{ height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { backgroundColor: '#FF6900' } }} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
                                                        {Math.round(outlineProgress)}%
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 2,
                                                            color: '#FF6900',
                                                            fontWeight: 700,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {CONTENT_GENERATION_STEPS[Math.min(contentProgress.stepIndex, CONTENT_GENERATION_STEPS.length - 1)]}
                                                    </Typography>
                                                    <Box sx={{ width: '80%', mt: 2, mb: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={contentProgress.progressPercent}
                                                            sx={{ height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { backgroundColor: '#FF6900' } }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                                                        {contentProgress.progressPercent}% ({contentProgress.completedCount}/{contentProgress.totalCount} sessions completed)
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            color: '#666',
                                                            textAlign: 'center',
                                                            fontSize:'11px',
                                                            maxWidth: '85%',
                                                        }}
                                                    >
                                                        (Take note that images for your content may take an additional few minutes to appear in the program slides after the text has been generated.)
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                            </Box>
                        )}

                        {/* Error overlay - covers card except button area at bottom */}
                        {hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 'calc(100% - 70px)', // Leave space for button at bottom
                                    backgroundColor: 'rgba(255, 235, 238, 0.95)',
                                    zIndex: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 3,
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 800, mb: 1 }}>
                                    {index === 0 ? 'Outline generation failed' : 'Content generation failed'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#b71c1c', mb: 2 }}>
                                    Something went wrong. Please try again.
                                </Typography>
                                {index === 0 && outlineStatus.failure_reason && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f', fontStyle: 'italic' }}>
                                        {outlineStatus.failure_reason}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <CardContent className={classes.cardContent}>
                            <div className={classes.cardHeader}>
                                <img src={step.icon} alt={step.title}
                                     style={{width: step.width, height: step.height, marginBottom: '20px', marginTop: '20px'}}/>
                                <Typography variant="h5"
                                            style={{color: '#fff', marginBottom: '10px'}}>{step.title}</Typography>
                            </div>
                            <Typography variant="body1" style={{
                                display: index === 0 && showLoader ? 'none' : 'block',
                                marginTop: '50px',
                                padding: '0 10px',
                                textAlign: 'center'
                            }}>{step.content}</Typography>

                            {index <= activeStep && (
                                <Box sx={{flexDirection:{sm:'row', xs:'column'}, zIndex: (hasError || (index === 1 && needsRebuild)) ? 4 : 1}} style={{display: 'flex', position: 'absolute', bottom: '25px', width:'95%',}}>
                                    {step.buttons.map((button, btnIndex) => (
                                            <Button
                                                key={btnIndex}
                                                className={classes.button}
                                                variant="contained"
                                                onClick={() => {
                                                    if (button.action) {
                                                        button.action();
                                                    } else if (button.onClick) {
                                                        button.onClick();
                                                    } else if (button.url) {
                                                        history.push({
                                                            pathname: button.url,
                                                            state: {
                                                                assessment: programData,
                                                                outlineStatus: outlineStatus,
                                                                dqcoList: dqcoList,
                                                                programCode: code,
                                                                // A school / network previewing its
                                                                // partner's program: view only.
                                                                viewOnly: isPartnerProgramForClient,
                                                            }
                                                        });
                                                    }
                                                }}
                                                disabled={(hasError || (index === 1 && needsRebuild)) ? false : disabled}
                                            >
                                                {button.label}
                                            </Button>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    )
                })}
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </div>
    );
};

export default DQProProgDashboardPage;